import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  GraduationCap,
  Users,
  UsersRound,
  CalendarCheck,
  Wallet,
  FileBarChart,
  Briefcase,
  Library,
  Bus,
  Layers,
  BookOpen,
  CalendarRange,
  Building2,
  Megaphone,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { SchoolStatsApi, AttendanceApi, AnnouncementsApi, ExamsApi, AcademicYearsApi } from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Input';
import { useTerminology } from '@/hooks/useTerminology';
import { isAdminRole } from '@/lib/access';

export function DashboardPage() {
  const { user, schoolSlug, orgToken } = useAuthStore();
  const term = useTerminology();
  const isAdmin = isAdminRole(user?.role);
  // An org admin acting-as-school session may point at a legacy school whose
  // tenant schema genuinely has no tables yet (`isSchemaProvisioned: false`
  // on the switch-school response — see AuthService.buildOrgActingSession()).
  // That's rare: 1 of 61 real schools today. Gating on `scope === 'school'`
  // alone (the original check) was wrong for the other 60/61, which have
  // real, fully-readable data — it showed "not yet migrated" even for
  // schools with real students/staff/attendance. SchoolStatsService (and
  // every other query this page makes) was rewritten against the real
  // migrated schema and no longer 500s for a provisioned school under
  // either scope — see docs/DEVIATIONS.md.
  const isUnprovisionedOrgSession = !!orgToken && user?.scope === 'school' && user?.isSchemaProvisioned === false;
  const { data: stats, isLoading } = useQuery({
    queryKey: ['school-stats'],
    queryFn: SchoolStatsApi.overview,
    enabled: !isUnprovisionedOrgSession,
  });

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const currentYearName = stats?.academicYears.current?.name;

  if (isUnprovisionedOrgSession) {
    return (
      <>
        <PageHeader
          title={`Welcome, ${user?.name ?? 'there'}`}
          description={`${schoolSlug ? schoolSlug.toUpperCase() : ''} · ${today}`}
        />
        <div className="card p-8 text-center">
          <p className="text-sm font-medium text-slate-700">
            School data not yet migrated
          </p>
          <p className="mt-1 text-sm text-slate-500">
            This school's records haven't been migrated into the platform yet.
            Check back once migration for this school is complete.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={`Welcome, ${user?.name ?? 'there'}`}
        description={`${schoolSlug ? schoolSlug.toUpperCase() : ''} · ${today}`}
        actions={
          currentYearName ? (
            <Badge tone="green">{currentYearName}</Badge>
          ) : (
            <Badge tone="amber">No current academic year</Badge>
          )
        }
      />

      {/* Hero stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <HeroStatCard to="/staff" icon={Briefcase} label="Total Teachers" value={stats?.teachers.total ?? 0} loading={isLoading} tone="indigo" />
        <HeroStatCard to="/students" icon={GraduationCap} label="Total Students" value={stats?.students.total ?? 0} loading={isLoading} tone="green" />
        <HeroStatCard to="/parents" icon={UsersRound} label="Total Guardians" value={stats?.guardians.total ?? 0} loading={isLoading} tone="indigo" />
        <HeroStatCard to="/setup/classes" icon={Layers} label={`Total ${term.levelPlural}`} value={stats?.classes.total ?? 0} loading={isLoading} tone="green" />
      </div>

      {/* Secondary stats */}
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard to="/setup/classes" icon={Users} label={term.groupPlural} value={stats?.sections.total ?? 0} loading={isLoading} tone="purple" />
        <StatCard to="/setup/subjects" icon={BookOpen} label="Subjects" value={stats?.subjects.total ?? 0} loading={isLoading} tone="amber" />
        <StatCard to="/setup/academic-years" icon={CalendarRange} label="Years" value={stats?.academicYears.total ?? 0} loading={isLoading} tone="green" />
      </div>

      {/* Attendance + exam result charts */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <AttendanceByClassCard />
        <ExamResultsCard />
      </div>

      {/* Recent announcements */}
      <div className="mt-6">
        <RecentAnnouncementsCard />
      </div>

      {/* Admin-only setup + quick links */}
      {isAdmin && (
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="card p-6 lg:col-span-2">
          <h3 className="mb-1 font-semibold text-slate-900">Getting started</h3>
          <p className="mb-4 text-sm text-slate-500">
            Complete these steps to get your school operational.
          </p>
          <ol className="space-y-3">
            <OnboardingStep
              num={1}
              title="Set up the current academic year"
              desc={`E.g. 2026-27 — required before adding ${term.levelPlural.toLowerCase()}.`}
              done={(stats?.academicYears.total ?? 0) > 0}
              link="/setup/academic-years"
            />
            <OnboardingStep
              num={2}
              title={`Add ${term.levelPlural.toLowerCase()} and ${term.groupPlural.toLowerCase()}`}
              desc={`${term.level} 1A, 1B… up to your highest level.`}
              done={(stats?.classes.total ?? 0) > 0}
              link="/setup/classes"
            />
            <OnboardingStep
              num={3}
              title={`Define subjects per ${term.level.toLowerCase()}`}
              desc="With max marks and pass marks."
              done={(stats?.subjects.total ?? 0) > 0}
              link="/setup/subjects"
            />
            <OnboardingStep
              num={4}
              title="Add staff (teachers)"
              desc="Each staff gets a login account."
              done={(stats?.teachers.total ?? 0) > 0}
              link="/staff"
            />
            <OnboardingStep
              num={5}
              title="Enroll your first students"
              desc="Admission number, parent details, photo."
              done={(stats?.students.total ?? 0) > 0}
              link="/students"
            />
          </ol>
        </div>

        <div className="card p-6">
          <h3 className="mb-4 font-semibold text-slate-900">Quick links</h3>
          <ul className="space-y-1">
            <QuickLink to="/students" icon={GraduationCap} label="Students" />
            <QuickLink to="/staff" icon={Briefcase} label="Staff" />
            <QuickLink
              to="/setup/classes"
              icon={Layers}
              label={`${term.levelPlural} & ${term.groupPlural}`}
            />
            <QuickLink to="/setup/subjects" icon={BookOpen} label="Subjects" />
            <QuickLink
              to="/setup/academic-years"
              icon={CalendarRange}
              label="Academic Years"
            />
          </ul>
        </div>
      </div>
      )}

      {/* Modules grid (admin) */}
      {isAdmin && (
      <div className="mt-6">
        <h3 className="mb-3 font-semibold text-slate-900">Modules</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <ModuleTile icon={CalendarCheck} label="Attendance" to="/attendance" />
          <ModuleTile icon={FileBarChart} label="Exams" to="/exams" />
          <ModuleTile icon={Wallet} label="Fees" to="/fees" />
          <ModuleTile icon={Library} label="Library" to="/library" />
          <ModuleTile icon={Bus} label="Transport" to="/transport" />
          <ModuleTile icon={Building2} label="Hostel" to="/hostel" />
          <ModuleTile icon={CalendarRange} label="Timetable" to="/timetable" />
          <ModuleTile icon={FileBarChart} label="Reports" to="/reports" />
        </div>
      </div>
      )}
    </>
  );
}

function StatCard({
  to,
  icon: Icon,
  label,
  value,
  subline,
  tone,
  loading,
}: {
  to: string;
  icon: typeof Users;
  label: string;
  value: number;
  subline?: string;
  tone: 'blue' | 'indigo' | 'purple' | 'amber' | 'green';
  loading?: boolean;
}) {
  const toneClass = {
    blue: 'bg-blue-100 text-blue-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    purple: 'bg-purple-100 text-purple-700',
    amber: 'bg-amber-100 text-amber-700',
    green: 'bg-green-100 text-green-700',
  }[tone];

  return (
    <Link
      to={to}
      className="card group block p-4 transition hover:border-slate-300 hover:shadow-md"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">
            {loading ? <span className="text-slate-300">—</span> : value}
          </div>
          {subline && (
            <div className="mt-0.5 text-[10px] text-slate-500">{subline}</div>
          )}
        </div>
        <div className={`rounded-md p-1.5 ${toneClass}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

/** Bigger "hero" stat card — Teachers/Students/Guardians/Classes at the top of the dashboard. */
function HeroStatCard({
  to,
  icon: Icon,
  label,
  value,
  tone,
  loading,
}: {
  to: string;
  icon: typeof Users;
  label: string;
  value: number;
  tone: 'indigo' | 'green';
  loading?: boolean;
}) {
  const toneClass = tone === 'indigo' ? 'bg-slate-800 text-white' : 'bg-emerald-500 text-white';
  return (
    <Link to={to} className="card group flex items-center justify-between p-5 transition hover:border-slate-300 hover:shadow-md">
      <div>
        <div className="text-sm text-slate-600">{label}</div>
        <div className="mt-2 text-3xl font-bold text-slate-900">{loading ? <span className="text-slate-300">—</span> : value}</div>
      </div>
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${toneClass}`}>
        <Icon className="h-6 w-6" />
      </div>
    </Link>
  );
}

/** Attendance % per section within a selected class — Recharts bar chart, same conventions as OrgOverviewPage's charts. */
function AttendanceByClassCard() {
  const term = useTerminology();
  const [classId, setClassId] = useState('');

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-dashboard-attendance'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];

  // The class with the most real attendance data — not just whichever class
  // a plain list happens to return first (which could easily have zero
  // attendance rows and show an empty chart on load). Wait for this query
  // to actually settle (isSuccess) before falling back to classes[0] —
  // otherwise a fast classes-list response would lock in classes[0] before
  // this one resolves.
  const { data: defaultClassId, isSuccess: defaultClassIdLoaded } = useQuery({
    queryKey: ['attendance-default-class'],
    queryFn: AttendanceApi.defaultClassId,
  });

  useEffect(() => {
    if (!classId && classes.length && defaultClassIdLoaded) {
      setClassId(defaultClassId || classes[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, defaultClassId, defaultClassIdLoaded]);

  const { data, isFetching } = useQuery({
    queryKey: ['attendance-by-class', classId],
    queryFn: () => AttendanceApi.byClass(classId),
    enabled: !!classId,
  });

  const chartData = (data?.sections ?? []).map((s) => ({ section: s.sectionName, percentage: s.percentage ?? 0 }));

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-900">Attendance</h2>
        <Select className="!w-44" value={classId} onChange={(e) => setClassId(e.target.value)}>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </div>
      {!classId || isFetching ? (
        <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">Loading…</div>
      ) : chartData.length === 0 ? (
        <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">
          No {term.groupPlural.toLowerCase()} found for this {term.level.toLowerCase()}.
        </div>
      ) : (
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="section" />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v: number) => [`${v}%`, 'Attendance']} />
              <Bar dataKey="percentage" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function OnboardingStep({
  num,
  title,
  desc,
  done,
  link,
}: {
  num: number;
  title: string;
  desc: string;
  done: boolean;
  link: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <div
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
          done ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
        }`}
      >
        {done ? '✓' : num}
      </div>
      <div className="flex-1">
        <Link
          to={link}
          className="text-sm font-medium text-slate-900 hover:text-brand-600"
        >
          {title}
        </Link>
        <div className="text-xs text-slate-500">{desc}</div>
      </div>
      <Badge tone={done ? 'green' : 'slate'}>
        {done ? 'Done' : 'Pending'}
      </Badge>
    </li>
  );
}

function QuickLink({
  to,
  icon: Icon,
  label,
}: {
  to: string;
  icon: typeof Users;
  label: string;
}) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
      >
        <Icon className="h-4 w-4 text-slate-400" />
        {label}
      </Link>
    </li>
  );
}

function ModuleTile({
  icon: Icon,
  label,
  to,
}: {
  icon: typeof Users;
  label: string;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="card flex flex-col items-center justify-center gap-2 p-4 text-center transition hover:border-brand-300 hover:bg-brand-50"
    >
      <Icon className="h-6 w-6 text-brand-600" />
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </Link>
  );
}

/** Class-by-class pass/fail stats for a selected published exam — Session Year + Exam Name dropdowns, stacked bar chart. Aggregates the already-correct exam_results rows; no pass/fail recompute here. */
function ExamResultsCard() {
  const [academicYearId, setAcademicYearId] = useState('');
  const [examId, setExamId] = useState('');

  const { data: years = [] } = useQuery({
    queryKey: ['academic-years', 'all-for-dashboard-exam-results'],
    queryFn: AcademicYearsApi.list,
  });

  useEffect(() => {
    if (!academicYearId && years.length) {
      setAcademicYearId(years.find((y) => y.isCurrent)?.id ?? years[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  const { data: exams = [] } = useQuery({
    queryKey: ['exams', 'published-for-dashboard', academicYearId],
    queryFn: () => ExamsApi.list({ academicYearId, status: 'published' }),
    enabled: !!academicYearId,
  });

  useEffect(() => {
    setExamId(exams[0]?.id ?? '');
  }, [exams]);

  const { data, isFetching } = useQuery({
    queryKey: ['exam-results-by-class', examId],
    queryFn: () => ExamsApi.resultsByClass(examId),
    enabled: !!examId,
  });

  const chartData = (data ?? []).map((c) => ({ className: c.className, Passed: c.passed, Failed: c.failed }));

  return (
    <div className="card p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-slate-900">Exam Results</h2>
        <div className="flex gap-2">
          <Select className="!w-36" value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Select>
          <Select className="!w-44" value={examId} onChange={(e) => setExamId(e.target.value)} disabled={exams.length === 0}>
            {exams.length === 0 ? (
              <option value="">No published exams</option>
            ) : (
              exams.map((ex) => (
                <option key={ex.id} value={ex.id}>
                  {ex.name}
                </option>
              ))
            )}
          </Select>
        </div>
      </div>
      {!examId || isFetching ? (
        <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">
          {exams.length === 0 ? 'No published exams for this year yet.' : 'Loading…'}
        </div>
      ) : chartData.length === 0 ? (
        <div className="flex h-[260px] items-center justify-center text-sm text-slate-400">No results found for this exam.</div>
      ) : (
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="className" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="Passed" stackId="a" fill="#22c55e" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Failed" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/** Latest announcements — No./Title/Description, links through to the full Announcements page. */
function RecentAnnouncementsCard() {
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['announcements-recent'],
    queryFn: () => AnnouncementsApi.recent(5),
  });

  return (
    <div className="card p-5">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
          <Megaphone className="h-4 w-4 text-slate-400" />
          Recent Announcements
        </h2>
        <Link to="/announcements" className="text-xs font-medium text-brand-600 hover:underline">
          View all
        </Link>
      </div>
      {isLoading ? (
        <div className="py-6 text-center text-sm text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-500">No announcements posted yet.</div>
      ) : (
        <table className="min-w-full divide-y divide-slate-100">
          <thead>
            <tr>
              <th className="w-10 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">No.</th>
              <th className="py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
              <th className="py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((a, i) => (
              <tr key={a.id}>
                <td className="py-2 text-sm text-slate-500">{i + 1}</td>
                <td className="py-2 text-sm font-medium text-slate-900">{a.title}</td>
                <td className="max-w-md truncate py-2 text-sm text-slate-600">{a.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
